import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Card,
  ListGroup,
  Badge,
  Spinner,
  Modal,
} from "react-bootstrap";
import WhatsAppPreview from "../../components/messaging/WhatsAppPreview";
import PageHeader from "../../components/common/PageHeader";
import {
  createActivityLog,
  fetchCategories,
  fetchContactsByCategory,
  fetchMessageTemplates,
  sendBulkWhatsAppCampaign,
  updateActivityLog,
} from "../../service/apiService";

export default function CampaignPage() {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedTemplateName, setSelectedTemplateName] =
    useState("sale_offer");
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [headerImageFile, setHeaderImageFile] = useState(null);
  const [headerImagePreviewUrl, setHeaderImagePreviewUrl] = useState(
    "https://surabhi.semicoloninnovations.in/company-logo.png"
  );
  const [params, setParams] = useState({
    param2: "",
    param3: "",
    param4: "",
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendSummary, setSendSummary] = useState(null);

  useEffect(() => {
    return () => {
      if (
        headerImagePreviewUrl &&
        headerImagePreviewUrl.startsWith("blob:")
      ) {
        URL.revokeObjectURL(headerImagePreviewUrl);
      }
    };
  }, [headerImagePreviewUrl]);

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const res = await fetchCategories();
        if (!isMounted || res.status !== "success") return;

        const nextCategories = res.data || [];
        setCategories(nextCategories);

        if (!selectedCategoryId && nextCategories.length > 0) {
          setSelectedCategoryId(String(nextCategories[0].id));
        }
      } finally {
        if (isMounted) {
          setLoadingCategories(false);
        }
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, [selectedCategoryId]);

  useEffect(() => {
    let isMounted = true;

    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const res = await fetchMessageTemplates();
        if (!isMounted || res.status !== "success") return;

        const nextTemplates = (res.data || []).filter((template) => {
          const components = Array.isArray(template.components)
            ? template.components
            : [];
          const hasImageHeader = components.some(
            (component) =>
              String(component.type || "").toUpperCase() === "HEADER" &&
              String(component.format || "").toUpperCase() === "IMAGE"
          );
          const hasBody = components.some(
            (component) => String(component.type || "").toUpperCase() === "BODY"
          );

          return hasImageHeader && hasBody;
        });

        setTemplates(nextTemplates);

        if (
          nextTemplates.length > 0 &&
          !nextTemplates.some(
            (template) => template.name === selectedTemplateName
          )
        ) {
          setSelectedTemplateName(nextTemplates[0].name);
        }
      } finally {
        if (isMounted) {
          setLoadingTemplates(false);
        }
      }
    };

    loadTemplates();

    return () => {
      isMounted = false;
    };
  }, [selectedTemplateName]);

  useEffect(() => {
    let isMounted = true;

    const loadContacts = async () => {
      if (!selectedCategoryId) {
        setContacts([]);
        setSelectedIds([]);
        return;
      }

      setLoadingContacts(true);
      try {
        const res = await fetchContactsByCategory(selectedCategoryId);
        if (!isMounted || res.status !== "success") return;

        const nextContacts = (res.data || []).filter(
          (c) => Number(c.is_opted_in ?? 1) === 1
        );
        setContacts(nextContacts);
        setSelectedIds(nextContacts.map((contact) => contact.id));
      } finally {
        if (isMounted) {
          setLoadingContacts(false);
        }
      }
    };

    loadContacts();

    return () => {
      isMounted = false;
    };
  }, [selectedCategoryId]);

  const selectedContacts = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return contacts
      .filter((contact) => selectedSet.has(contact.id))
      .map((contact) => ({
        name: contact.name,
        phone: contact.phone,
      }));
  }, [contacts, selectedIds]);

  const selectedTemplate = useMemo(
    () =>
      templates.find((template) => template.name === selectedTemplateName) ||
      null,
    [templates, selectedTemplateName]
  );

  const templateBodyText = String(selectedTemplate?.components?.find(
    (component) => String(component.type || "").toUpperCase() === "BODY"
  )?.text || "");

  const templateLanguage = selectedTemplate?.language || "en";

  const fieldLabels = useMemo(() => {
    const placeholders = [
      { key: "param2", token: "{{2}}", fallback: "Parameter 1" },
      { key: "param3", token: "{{3}}", fallback: "Parameter 2" },
      { key: "param4", token: "{{4}}", fallback: "Parameter 3" },
    ];

    return placeholders.map((item) => {
      const line = templateBodyText
        .split("\n")
        .find((candidate) => candidate.includes(item.token)) || "";
      const [beforeToken, afterToken] = line.split(item.token);
      const labelFromBody = (beforeToken || afterToken || "")
        .replace(/^[^A-Za-z0-9]+/, "")
        .replace(/[:\s.-]+$/, "")
        .trim();

      return {
        key: item.key,
        label: labelFromBody || item.fallback,
        placeholder: labelFromBody || item.fallback,
      };
    });
  }, [templateBodyText]);

  const toggleContact = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === contacts.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(contacts.map((c) => c.id));
  };

  const handleParamChange = (event) => {
    const { name, value } = event.target;
    setParams((current) => ({ ...current, [name]: value }));
  };

  const handleHeaderImageChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (headerImagePreviewUrl && headerImagePreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(headerImagePreviewUrl);
    }

    setHeaderImageFile(file);
    setHeaderImagePreviewUrl(file ? URL.createObjectURL(file) : "");
  };

  const handleSendCampaign = async () => {
    if (!selectedCategoryId) {
      alert("Please select a category first");
      return;
    }

    if (!headerImageFile) {
      alert("Please choose a header image");
      return;
    }

    if (Object.values(params).some((value) => !value.trim())) {
      alert("Please fill in all template parameters");
      return;
    }

    if (!selectedContacts.length) {
      alert("Please select at least one contact");
      return;
    }

    const summaryContext = {
      category_id: selectedCategoryId,
      selected_contacts: selectedContacts.length,
      template_name: selectedTemplateName,
      template_language: templateLanguage,
    };

    let activityLogId = null;

    setSending(true);
    setSendSummary(null);

    try {
      const startLog = await createActivityLog({
        action: "WHATSAPP_CAMPAIGN_STARTED",
        message: "WhatsApp campaign started",
        context: summaryContext,
        summary: {
          status: "started",
          total: selectedContacts.length,
          sent: 0,
          failed: 0,
          selected_contacts: selectedContacts.length,
        },
      });

      if (startLog?.status === "success") {
        activityLogId = startLog?.data?.id || null;
      }
    } catch (logError) {
      console.warn("Unable to create campaign log:", logError);
    }

    try {
      const res = await sendBulkWhatsAppCampaign({
        selected_contacts: selectedContacts,
        header_image_file: headerImageFile,
        template_name: selectedTemplateName,
        template_language: templateLanguage,
        ...Object.fromEntries(
          Object.entries(params).map(([key, value]) => [key, value.trim()])
        ),
      });

      if (res.status === "success") {
        const successCount = res.summary?.success ?? 0;
        const failedCount = res.summary?.failed ?? 0;
        const finalStatus = failedCount > 0 ? "WHATSAPP_CAMPAIGN_COMPLETED_WITH_ERRORS" : "WHATSAPP_CAMPAIGN_COMPLETED";
        const finalMessage =
          failedCount > 0
            ? "WhatsApp campaign completed with some failures"
            : "WhatsApp campaign completed successfully";

        setSendSummary({
          total: res.summary?.total ?? selectedContacts.length,
          success: successCount,
          failed: failedCount,
          results: res.results || [],
        });

        if (activityLogId) {
          try {
            await updateActivityLog({
              id: activityLogId,
              action: finalStatus,
              message: finalMessage,
              context: summaryContext,
              summary: {
                status: failedCount > 0 ? "completed_with_errors" : "completed",
                total: res.summary?.total ?? selectedContacts.length,
                success: successCount,
                failed: failedCount,
                selected_contacts: selectedContacts.length,
                response_message: res.message || "",
              },
            });
          } catch (logError) {
            console.warn("Unable to update campaign log:", logError);
          }
        }

        alert(
          `Campaign sent. Success: ${successCount}, Failed: ${failedCount}`
        );
      } else {
        if (activityLogId) {
          try {
            await updateActivityLog({
              id: activityLogId,
              action: "WHATSAPP_CAMPAIGN_FAILED",
              message: res.message || "WhatsApp campaign failed",
              context: summaryContext,
              summary: {
                status: "failed",
                selected_contacts: selectedContacts.length,
                response_message: res.message || "",
              },
            });
          } catch (logError) {
            console.warn("Unable to update campaign log:", logError);
          }
        }

        alert(res.message || "Failed to send campaign");
      }
    } catch (error) {
      if (activityLogId) {
        try {
          await updateActivityLog({
            id: activityLogId,
            action: "WHATSAPP_CAMPAIGN_FAILED",
            message: "Server error while sending campaign",
            context: summaryContext,
            summary: {
              status: "failed",
              selected_contacts: selectedContacts.length,
              response_message: "Server error while sending campaign",
            },
          });
        } catch (logError) {
          console.warn("Unable to update campaign log:", logError);
        }
      }

      alert("Server error while sending campaign");
    } finally {
      setSending(false);
    }
  };

  const openSendConfirm = () => {
    if (!selectedContacts.length) {
      alert("Please select at least one contact");
      return;
    }

    setShowSendConfirm(true);
  };

  const confirmSendCampaign = async () => {
    setShowSendConfirm(false);
    await handleSendCampaign();
  };

  return (
    <Container fluid className="admin-page">
      <PageHeader
        title="Send WhatsApp Campaign"
        subtitle="Select a category, deselect any numbers, then send the message"
      />

      <Row className="g-3 align-items-start">
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Select Category</strong>
                <div>{loadingCategories && <Spinner animation="border" size="sm" />}</div>
              </div>

              <ListGroup className="mb-3" variant="flush">
                {categories.map((category) => (
                  <ListGroup.Item
                    key={category.id}
                    action
                    active={String(category.id) === selectedCategoryId}
                    onClick={() => setSelectedCategoryId(String(category.id))}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>{category.name}</span>
                    <Badge
                      bg={String(category.id) === selectedCategoryId ? "light" : "secondary"}
                      text={String(category.id) === selectedCategoryId ? "dark" : "light"}
                    >
                      {category.contact_count ?? 0}
                    </Badge>
                  </ListGroup.Item>
                ))}
                {!loadingCategories && categories.length === 0 && (
                  <ListGroup.Item className="text-muted">
                    No categories found
                  </ListGroup.Item>
                )}
              </ListGroup>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>
                  {selectedCategoryId
                    ? `Contacts in ${
                        categories.find(
                          (category) => String(category.id) === selectedCategoryId
                        )?.name || "selected category"
                      }`
                    : "Contacts"}
                </strong>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="light" text="dark">
                    Selected: {selectedIds.length}
                  </Badge>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={toggleSelectAll}
                    disabled={!contacts.length}
                  >
                    {selectedIds.length === contacts.length ? "Clear All" : "Select All"}
                  </Button>
                </div>
              </div>

              <ListGroup style={{ maxHeight: "62vh", overflowY: "auto" }}>
                {loadingContacts && (
                  <ListGroup.Item className="text-muted">
                    Loading contacts...
                  </ListGroup.Item>
                )}
                {!loadingContacts &&
                  contacts.map((contact) => (
                    <ListGroup.Item
                      key={contact.id}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <Form.Check
                        id={`contact-${contact.id}`}
                        label={`${contact.name} (${contact.phone})`}
                        checked={selectedIds.includes(contact.id)}
                        onChange={() => toggleContact(contact.id)}
                      />
                    </ListGroup.Item>
                  ))}
                {!loadingContacts && selectedCategoryId && !contacts.length && (
                  <ListGroup.Item className="text-muted">
                    No opted-in contacts found
                  </ListGroup.Item>
                )}
                {!loadingContacts && !selectedCategoryId && (
                  <ListGroup.Item className="text-muted">
                    Pick a category to load contacts
                  </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col md={5}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Message Template</Form.Label>
                <Form.Select
                  value={selectedTemplateName}
                  onChange={(e) => setSelectedTemplateName(e.target.value)}
                  disabled={loadingTemplates || !templates.length}
                >
                  {templates.map((template) => (
                    <option key={template.id || template.name} value={template.name}>
                      {template.name} ({template.language})
                    </option>
                  ))}
                </Form.Select>
                {/* <Form.Text className="text-muted">
                  Choose an approved image template for this campaign.
                </Form.Text> */}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Header Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleHeaderImageChange}
                />
                {/* <Form.Text className="text-muted">
                  Choose the banner image to upload. The backend will store it
                  and send the public URL to WhatsApp.
                </Form.Text> */}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{fieldLabels[0]?.label || "Parameter 1"}</Form.Label>
                <Form.Control
                  name="param2"
                  placeholder={fieldLabels[0]?.placeholder || "Value"}
                  value={params.param2}
                  onChange={handleParamChange}
                />
                {/* <Form.Text className="text-muted">
                  {templateBodyText || "This field will update based on the selected template."}
                </Form.Text> */}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{fieldLabels[1]?.label || "Parameter 2"}</Form.Label>
                <Form.Control
                  name="param3"
                  placeholder={fieldLabels[1]?.placeholder || "Value"}
                  value={params.param3}
                  onChange={handleParamChange}
                />
                {/* <Form.Text className="text-muted">
                  Use the second variable from the template body.
                </Form.Text> */}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{fieldLabels[2]?.label || "Parameter 3"}</Form.Label>
                <Form.Control
                  name="param4"
                  placeholder={fieldLabels[2]?.placeholder || "Value"}
                  value={params.param4}
                  onChange={handleParamChange}
                />
                {/* <Form.Text className="text-muted">
                  Use the third variable from the template body.
                </Form.Text> */}
              </Form.Group>

              <div className="d-flex flex-wrap gap-3 align-items-center">
                <Button variant="success" onClick={openSendConfirm} disabled={sending}>
                  {sending ? "Sending..." : "Send Campaign"}
                </Button>

                {sendSummary && (
                  <div className="small text-muted">
                    Total: {sendSummary.total || 0} | Success: {sendSummary.success || 0} | Failed:{" "}
                    {sendSummary.failed || 0}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <div className="position-sticky" style={{ top: "1rem" }}>
            <WhatsAppPreview
              headerImageUrl={headerImagePreviewUrl}
              params={params}
              contactName={selectedContacts[0]?.name || "Rahul"}
              template={selectedTemplate}
            />
          </div>
        </Col>
      </Row>

      <Modal
        show={showSendConfirm}
        onHide={() => setShowSendConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Campaign</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Are you sure you want to send this campaign to{" "}
          <strong>{selectedContacts.length}</strong> contact
          {selectedContacts.length === 1 ? "" : "s"}?
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setShowSendConfirm(false)}
          >
            Cancel
          </Button>
          <Button variant="success" onClick={confirmSendCampaign}>
            Yes, Send
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
